<template>
  <HeroSection :title="hero.title" :description="hero.description" :type="'customer'" />
  <TrustedBySection
    data-aos="fade-up"
    data-aos-once="true"
    :description="trustedBy.description"
    :link="{ text: trustedBy.linkText, href: trustedBy.linkHref }"
  />

  <CustomersReview
    data-aos="fade-up"
    data-aos-once="true"
    :title="feature.title"
    :description="feature.description"
    :items="feature.items"
  />
  <BookConsultationShortSection
    data-aos="fade-up"
    data-aos-once="true"
    :title="bookShort.title"
    :description="bookShort.description"
    :button-text="'Book a Consultation'"
  />
  <CustomersReview data-aos="fade-up" data-aos-once="true" :items="doctors.items" />
  <BookConsultationSection
    data-aos="fade-up"
    data-aos-once="true"
    :title="book.title"
    :description="book.description"
    :items="book.items"
  />
  <BlogSection data-aos="fade-up" data-aos-once="true"></BlogSection>
</template>

<script lang="ts" setup>
import { BookItemsType } from '@/types/book-types'
import { CustomerItemsType } from '@/types/customers-types'

useCustomServerMeta({
  title: 'Inspiring Stories by Real Credsy Platform Customers',
  description:
    'Trusted by 16,000+ healthcare providers and organizations who using our medical licensing and credentialing platform.',
})

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [{ name: 'Credsy', item: '/' }, { name: 'Customer Stories' }],
  }),
])

const bookShort: {
  title: string
  description: string
} = {
  title: `Book Your Consultation&nbsp;Today`,
  description: 'And pick our brain for free. Zero commitments.',
}

const hero: {
  title: string
  description: string
} = {
  title: `Inspiring stories, real Credsy <span class="line">customers</span>`,
  description:
    'Explore how our diverse network of customers is using our medical licensing and credentialing platform to advance their goals.',
}

const trustedBy: {
  description: string
  linkText: string
  linkHref: string
} = {
  description: 'We reduce licensing and credentialing turnaround time by 70%.',
  linkText: 'Our Customers Stories',
  linkHref: '/',
}

const doctors: {
  items: CustomerItemsType
} = {
  items: [
    {
      title: `“They've been very responsive to my emails, saved me a ton of time and money”`,
      description: `
        <p>"I'm a board-certified internist and allergist, and I do a lot of consulting, that has required me to get multiple state licenses, including some very tough ones like California and Texas. The whole entire group has been with me every step of the way. We are currently working on other licenses with them, and I can't recommend them enough."</p>
      `,
      avatar: 'avatar-first.svg',
      avatar_text: 'Chet Tharpe',
      avatar_job: 'Medical Doctor, Orthopaedic Surgery',
      colorClasses: 'bg-sand-100',
      imageName: 'doc2.jpg',
      id: 4,
    },
    {
      title: `“They took care of everything. Excellent job.”`,
      description: `
        <p>"They were very efficient, aggressive, and followed my application through medical licensure. I didn’t have the time to pursue anything myself. I’d recommend them to anyone who needs this service."</p>
      `,
      avatar: 'avatar-sec.svg',
      avatar_text: 'Samir Wassef',
      avatar_job: 'Medical Doctor, Surgery',
      colorClasses: 'bg-blue-50',
      imageName: 'doc3.jpg',
      id: 3,
    },
  ],
}

const feature: {
  title: string
  description: string
  items: CustomerItemsType
} = {
  title: `Why healthcare providers and organizations trust us`,
  description: `Take a look at what our previous customers have to say…`,
  items: [
    {
      title: `“Instead of getting my license in 6 months, I got it in 2 months with no problems”`,
      description: `
        <p>"This whole process was very well organized, and at every single step, I was aware of what was going on. And the license arrived much sooner than I expected."</p>

      `,
      avatar: 'avatar-first.svg',
      avatar_text: 'Gennadiy Grigoryan',
      avatar_job: 'Medical Doctor, Orthopaedic Surgery',
      colorClasses: 'bg-sand-100',
      imageName: 'doc1.jpg',
      id: 1,
    },
    {
      title: `“I couldn't be happier with the level of service, the professionalism, the speedy responses”`,
      description: `
        <p>"Truly, this was a painless and pleasant experience. I was on a tight timeline, which was more so of an issue because of the COVID crisis. But medical licensing did come through and I'm very happy because it got it done."</p>
      `,
      avatar: 'avatar-sec.svg',
      avatar_text: 'Dr. Karim Sami Trad',
      avatar_job: 'Medical Doctor, Surgery',
      colorClasses: 'bg-blue-50',
      imageName: 'doc4.jpg',
      id: 2,
    },
  ],
}

const book: {
  title: string
  description: string
  items: BookItemsType
} = {
  title: `<span>Got questions?</span><span>You can schedule a free consultation</span>`,
  description: 'Book a free consultation with one of our licensing experts to see how we can help you can:',
  items: [
    'Gain visibility into provider status and application process',
    'Reduce costs and admin work',
    'License your providers faster',
  ],
}
</script>
